import { BORNES_PLACE_GARDEE } from '@nextilt/commun';
import { placeDe } from './planDeLaCarte';

/**
 * Déplacer une bulle sur la carte, cadenas ouvert (chantier deplacer-les-bulles, ticket 03,
 * ADR 0016) : les règles pures, sans React ni réseau, testées dans
 * `tests/client/carte/deplacerUneBulle.test.ts`.
 *
 * **Le client ne place rien** (ADR 0008) : pendant le geste, il montre la bulle, et ce qu'elle
 * porte, là où la main l'emmène ; au lâcher, il envoie son décalage par rapport au centre de sa
 * mère, à la profondeur affichée, et redemande la carte. C'est le serveur qui range le reste.
 */

export interface Point {
  x: number;
  y: number;
}

/** Ce qui décide si une bulle se glisse, ou si le glisser bouge le regard comme cadenas fermé. */
export interface SituationDuGlisser {
  cadenasOuvert: boolean;
  /** Lier ou Hiérarchiser : un clic désigne, rien ne se déplace. */
  enDesignation: boolean;
  /** Regrouper, en Analyse : les colonnes ne sont pas la carte du serveur. */
  enColonnes: boolean;
  /** Le centre ne se déplace pas : on est dedans. */
  estLeCentre: boolean;
  /** Une bulle cachée dans une étape fermée n'est pas à l'écran. */
  cachee: boolean;
}

export function peutSeGlisser(s: SituationDuGlisser): boolean {
  return s.cadenasOuvert && !s.enDesignation && !s.enColonnes && !s.estLeCentre && !s.cachee;
}

/**
 * « Remettre à sa place » dans le « ⋯ » (chantier deplacer-les-bulles, ticket 04) : seulement pour
 * une bulle à l'écran que le serveur a posée à sa place gardée, pour la profondeur affichée ; jamais
 * en Regrouper, où les colonnes remplacent la carte. Cadenas ouvert ou fermé, peu importe.
 */
export function peutSeRemettreASaPlace(place: (Point & { fixee: boolean }) | undefined, enColonnes: boolean): boolean {
  return !enColonnes && place?.fixee === true;
}

/**
 * La bulle et tout ce qu'elle porte **à l'écran** : ses descendantes qui ont une place. Une étape
 * fermée n'a pas de place pour ce qu'elle cache : ce qui est dedans attend sur elle et la suit.
 */
export function ceQuEmporte(
  id: string,
  enfantsDe: ReadonlyMap<string, readonly string[]>,
  places: Readonly<Record<string, Point>>
): Set<string> {
  const emportees = new Set<string>();
  const pile = [id];
  while (pile.length > 0) {
    const courant = pile.pop()!;
    if (emportees.has(courant) || !(courant in places)) continue;
    emportees.add(courant);
    pile.push(...(enfantsDe.get(courant) ?? []));
  }
  return emportees;
}

/** Les places montrées pendant le geste : ce qui est emporté suit la main, le reste ne bouge pas. */
export function placesGlissees<P extends Point>(
  places: Readonly<Record<string, P>>,
  emportees: ReadonlySet<string>,
  ecart: Point
): Readonly<Record<string, P>> {
  if (emportees.size === 0) return places;
  const vues: Record<string, P> = { ...places };
  for (const id of emportees) {
    const p = places[id];
    if (p) vues[id] = { ...p, x: p.x + ecart.x, y: p.y + ecart.y };
  }
  return vues;
}

const borne = (n: number) =>
  Math.min(BORNES_PLACE_GARDEE.decalageMax, Math.max(-BORNES_PLACE_GARDEE.decalageMax, Math.round(n)));

/**
 * Le décalage à envoyer au lâcher : la place où la bulle est lâchée (la sienne plus l'écart de la
 * main, sur la carte) moins le centre de sa mère tel qu'il est vu, en entiers, dans les bornes que
 * le serveur accepte. Au premier tour, la mère est le centre de la carte, en 0, 0.
 */
export function decalageAuLacher(
  id: string,
  places: Readonly<Record<string, Point>>,
  parId: ReadonlyMap<string, { parentId: string | null }>,
  centreId: string,
  ecart: Point
): { dx: number; dy: number } {
  const ici = placeDe(id, places, parId, centreId);
  const mereId = parId.get(id)?.parentId ?? centreId;
  const mere = mereId === centreId ? { x: 0, y: 0 } : placeDe(mereId, places, parId, centreId);
  return { dx: borne(ici.x + ecart.x - mere.x), dy: borne(ici.y + ecart.y - mere.y) };
}
